import axios from "axios";
import { Base_URL } from "../Client/apiURL";

axios.defaults.withCredentials = true;

const SESSION_INVALID_CODE = 501;
const SESSION_INVALID_STATUSES = new Set([401, 403, 440, 419, SESSION_INVALID_CODE]);
let axiosInterceptorId = null;
let isFetchWrapped = false;
let isLoggingOut = false;

const handleSessionExpiry = (logout, source = "unknown") => {
  if (typeof logout !== "function") return;
  if (isLoggingOut) return;

  isLoggingOut = true;
  console.warn(`Session expired detected from ${source}. Logging out user.`);

  Promise.resolve()
    .then(() => logout({ reason: "session-expired" }))
    .catch((err) => console.error("Error while logging out after session expiry:", err))
    .finally(() => {
      // Allow future logout attempts after state settles
      setTimeout(() => {
        isLoggingOut = false;
      }, 0);
    });
};

const shouldLogout = ({ status, responseCode, message }) => {
  if (SESSION_INVALID_STATUSES.has(status)) return true;
  if (responseCode === SESSION_INVALID_CODE) return true;
  if (typeof message === "string" && message.toLowerCase().includes("session invalid")) return true;
  return false;
};

const getOrigin = (url) => {
  if (!url) return "";
  try {
    return new URL(url, window.location.origin).origin;
  } catch (error) {
    return "";
  }
};

const shouldInspectAuthResponse = (url) => {
  if (typeof window === "undefined") return true;
  if (!url) return true;

  const requestOrigin = getOrigin(url);
  const appOrigin = Base_URL ? getOrigin(Base_URL) : "";

  if (!requestOrigin) return true;
  if (appOrigin && requestOrigin === appOrigin) return true;
  return requestOrigin === window.location.origin;
};

const attachAxiosInterceptor = (logout) => {
  if (axiosInterceptorId !== null) return;

  axiosInterceptorId = axios.interceptors.response.use(
    (response) => {
      const { data, status } = response || {};
      if (!shouldInspectAuthResponse(response?.config?.url)) return response;
      if (
        shouldLogout({
          status,
          responseCode: data?.responseCode,
          message: data?.message,
        })
      ) {
        handleSessionExpiry(logout, "axios:response");
      }
      return response;
    },
    (error) => {
      const status = error?.response?.status;
      const data = error?.response?.data;
      if (!shouldInspectAuthResponse(error?.config?.url)) return Promise.reject(error);
      if (
        shouldLogout({
          status,
          responseCode: data?.responseCode,
          message: data?.message,
        })
      ) {
        handleSessionExpiry(logout, "axios:error");
      }
      return Promise.reject(error);
    }
  );
};

const attachFetchInterceptor = (logout) => {
  if (isFetchWrapped) return;
  if (typeof window === "undefined" || typeof window.fetch !== "function") return;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (...args) => {
    const response = await originalFetch(...args);
    const requestUrl = args[0]?.url || args[0];

    if (!shouldInspectAuthResponse(requestUrl)) {
      return response;
    }

    const status = response?.status;
    if (SESSION_INVALID_STATUSES.has(status)) {
      handleSessionExpiry(logout, "fetch:status");
      return response;
    }

    try {
      const clonedResponse = response.clone();
      const contentType = clonedResponse.headers?.get("content-type") || "";
      if (contentType.includes("application/json")) {
        const data = await clonedResponse.json();
        if (
          shouldLogout({
            status,
            responseCode: data?.responseCode,
            message: data?.message,
          })
        ) {
          handleSessionExpiry(logout, "fetch:body");
        }
      }
    } catch (err) {
      // Ignore body parsing errors to avoid breaking consumers
    }

    return response;
  };

  isFetchWrapped = true;
};

export default function AuthInterceptor(logout) {
  attachAxiosInterceptor(logout);
  attachFetchInterceptor(logout);
}
